const COMA = ','
const DASH = '-'
const NUMBER_REGEX = /\d+/g

export const MAX_LEVELS_PER_BUILDING = 190

// Leaves room to number a whole building before `index + 1` stops advancing
const MAX_DENIED_INDEX = Number.MAX_SAFE_INTEGER - MAX_LEVELS_PER_BUILDING

export interface IDeniedRange {
  from: number
  to: number
}

const parseExpression = (exp: string): IDeniedRange => {
  if (!exp.includes(DASH)) {
    const expMatch = exp.match(NUMBER_REGEX)
    if (!expMatch) {
      return null
    }
    const index = Number(expMatch.join(''))
    return index > MAX_DENIED_INDEX ? null : { from: index, to: index }
  }

  const [fromExp, toExp] = exp.split(DASH)
  const fromMatch = fromExp.match(NUMBER_REGEX)
  const toMatch = toExp.match(NUMBER_REGEX)
  if (!fromMatch || !toMatch) {
    return null
  }
  const from = Number(fromMatch.join(''))
  const to = Number(toMatch.join(''))
  return from > to || to > MAX_DENIED_INDEX ? null : { from, to }
}

/** Ranges, not expanded indexes: a range shifts the numbering, so `1-2000` starts at 2001. */
export function getDeniedRanges(
  skipLevelIndexTemplate: string,
): IDeniedRange[] {
  const ranges = skipLevelIndexTemplate
    .split(COMA)
    .map(parseExpression)
    .filter(Boolean)
    .sort((a, b) => a.from - b.from)

  return ranges.reduce<IDeniedRange[]>((merged, range) => {
    const last = merged[merged.length - 1]
    if (last && range.from <= last.to + 1) {
      last.to = Math.max(last.to, range.to)
      return merged
    }
    merged.push({ ...range })
    return merged
  }, [])
}

export function nextAllowedIndex(
  after: number,
  deniedRanges: IDeniedRange[],
): number {
  let index = after + 1

  for (const range of deniedRanges) {
    if (index < range.from) {
      break
    }
    if (index <= range.to) {
      index = range.to + 1
    }
  }

  return index
}

export function nextAllowedIndexes(
  after: number,
  count: number,
  deniedRanges: IDeniedRange[],
): number[] {
  const indexes: number[] = []
  let index = after

  for (let i = 0; i < count; i++) {
    index = nextAllowedIndex(index, deniedRanges)
    indexes.push(index)
  }

  return indexes
}

/** Highest index first; denied ranges apply to the ground level and above. */
export function getLevelIndexes(
  levelCount: number,
  groundLevelPosition: number,
  isGroundIndexZero: boolean,
  deniedRanges: IDeniedRange[],
): number[] {
  const indexes: number[] = []

  for (let topIndex = -groundLevelPosition; topIndex < 0; topIndex++) {
    indexes.unshift(topIndex)
  }

  nextAllowedIndexes(
    isGroundIndexZero ? -1 : 0,
    levelCount - groundLevelPosition,
    deniedRanges,
  ).forEach(index => indexes.unshift(index))

  return indexes
}
